import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { useStore } from "../store";
import { severityColor, statusStyle } from "../data/mockData";
import { useIncidents } from "../api/sitroom";
import { SlaBar } from "../components/SlaBar";
import { RoleGuard } from "../components/RoleGuard";
import { ObserverBanner } from "../components/ObserverBanner";
import { IncidentFilters, buildBackendQuery, useFilterDirectories } from "../components/IncidentFilters";
import { IncidentFilterPresets } from "../components/IncidentFilterPresets";
import { IncidentsKanban } from "../components/IncidentsKanban";
import { useT } from "../lib/i18n";
import { TranslatableText } from "../components/TranslatableText";

const Row = ({ inc, onClick }) => {
  const { t } = useT();
  const sev = severityColor(inc.severity);
  const stStyle = statusStyle(inc.status);
  const slaPct = Number.isFinite(inc.sla) ? inc.sla : 0;
  const slaColor = slaPct > 100 ? "#E24B4A" : slaPct > 75 ? "#f39c12" : "#185FA5";
  const slaText = slaPct > 100 ? "BREACH" : `${slaPct}%`;

  return (
    <>
      {/* Desktop row */}
      <div
        className="row-hover hidden sm:block cursor-pointer"
        style={{
          background: "#0d0f18",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 6,
          marginBottom: 6,
          overflow: "hidden",
        }}
        onClick={onClick}
        data-testid={`incident-row-${inc.id}`}
      >
        <div
          className="grid items-center gap-3 px-4 py-3"
          style={{
            gridTemplateColumns: "5px 110px 1fr 90px 130px 160px 80px",
          }}
        >
        <div style={{ width: 5, alignSelf: "stretch", background: sev, borderRadius: 2 }} />
        <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0" }}>{inc.id}</div>
        <div className="min-w-0">
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "#f0f2f8" }}>
            <TranslatableText text={inc.title} testId={`incident-title-${inc.id}`} />
          </div>
          <div className="truncate" style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#c5cad8", marginTop: 2 }}>
            {inc.parties}
          </div>
        </div>
        <span
          style={{
            background: `${sev}22`,
            border: `1px solid ${sev}80`,
            color: sev,
            fontFamily: "DM Mono, monospace",
            fontSize: 9,
            padding: "2px 7px",
            borderRadius: 3,
            letterSpacing: "1px",
            justifySelf: "start",
          }}
        >
          {inc.severity.toUpperCase()}
        </span>
        <span
          style={{
            background: stStyle.bg,
            border: `1px solid ${stStyle.border}`,
            color: stStyle.color,
            fontFamily: "DM Mono, monospace",
            fontSize: 9,
            padding: "2px 7px",
            borderRadius: 3,
            letterSpacing: "1px",
            justifySelf: "start",
          }}
        >
          {t(`status.${inc.status}`)}
        </span>
        <div className="flex items-center gap-2">
          <div className="relative rounded-full overflow-hidden" style={{ width: 80, height: 5, background: "#181c2a" }}>
            <div
              className="absolute top-0 bottom-0 left-0"
              style={{ width: `${Math.min(slaPct, 100)}%`, background: slaColor }}
            />
          </div>
          <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: slaColor, minWidth: 60 }}>
            {slaText}
          </span>
        </div>
        <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#c5cad8" }}>{inc.age}</div>
        </div>
        <SlaBar createdAt={inc.createdAt} severity={inc.severity} status={inc.status} height={2} />
      </div>

      {/* Mobile card */}
      <div
        className="sm:hidden row-hover cursor-pointer"
        style={{
          background: "#0d0f18",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 6,
          marginBottom: 6,
          overflow: "hidden",
        }}
        onClick={onClick}
        data-testid={`incident-card-${inc.id}`}
      >
        <div className="flex gap-3 p-3">
        <div style={{ width: 5, alignSelf: "stretch", background: sev, borderRadius: 2 }} />
        <div className="flex-1 min-w-0">
          <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, color: "#f0f2f8" }}>
            <TranslatableText text={inc.title} testId={`incident-title-mobile-${inc.id}`} />
          </div>
          <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#c5cad8", marginTop: 2 }}>
            {inc.parties}
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span
              style={{
                background: `${sev}22`,
                border: `1px solid ${sev}80`,
                color: sev,
                fontFamily: "DM Mono, monospace",
                fontSize: 9,
                padding: "2px 7px",
                borderRadius: 3,
                letterSpacing: "1px",
              }}
            >
              {inc.severity.toUpperCase()}
            </span>
            <span
              style={{
                background: stStyle.bg,
                border: `1px solid ${stStyle.border}`,
                color: stStyle.color,
                fontFamily: "DM Mono, monospace",
                fontSize: 9,
                padding: "2px 7px",
                borderRadius: 3,
                letterSpacing: "1px",
              }}
            >
              {t(`status.${inc.status}`)}
            </span>
            <div className="flex items-center gap-1.5 ml-auto">
              <div className="relative rounded-full overflow-hidden" style={{ width: 60, height: 4, background: "#181c2a" }}>
                <div
                  className="absolute top-0 bottom-0 left-0"
                  style={{ width: `${Math.min(slaPct, 100)}%`, background: slaColor }}
                />
              </div>
              <span style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: slaColor }}>{slaText}</span>
            </div>
          </div>
        </div>
        </div>
        <SlaBar createdAt={inc.createdAt} severity={inc.severity} status={inc.status} height={2} />
      </div>
    </>
  );
};

export default function Incidents() {
  const incidentFilter = useStore((s) => s.incidentFilter);
  const setIncidentFilter = useStore((s) => s.setIncidentFilter);
  const openPanel = useStore((s) => s.openPanel);
  const [params] = useSearchParams();
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Iter37 — list/kanban toggle; persist in localStorage.
  const [viewMode, setViewMode] = useState(() => {
    const v = typeof window !== "undefined" ? localStorage.getItem("incidents_view_mode") : null;
    return v === "kanban" ? "kanban" : "list";
  });
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("incidents_view_mode", viewMode);
  }, [viewMode]);

  // Severity pill is UI-only (kept in store); all advanced filters live in URL
  // via useIncidentFilters / IncidentFilters.
  const urlSev = params.get("sev");
  const urlSla = params.get("sla");

  // Build backend query string from URL params (skipping UI-only `sev`/`sla`)
  const backendQuery = useMemo(() => buildBackendQuery(params), [params]);

  const { data: incidents = [] } = useIncidents(backendQuery);
  // Unfiltered total — for the "Showing X of Y" count, we need a baseline.
  const { data: allIncidents = [] } = useIncidents();
  const { tenants, plants } = useFilterDirectories();
  const { t } = useT();

  const filterPills = useMemo(() => {
    const byS = { s1: 0, s2: 0, s3: 0, s4: 0 };
    for (const i of allIncidents) {
      if (byS[i.severity] !== undefined) byS[i.severity] += 1;
    }
    return [
      { id: "all", label: t("incidents.filter_all"), count: allIncidents.length },
      { id: "s1", label: "S1", count: byS.s1 },
      { id: "s2", label: "S2", count: byS.s2 },
      { id: "s3", label: "S3", count: byS.s3 },
      { id: "s4", label: "S4", count: byS.s4 },
    ];
  }, [allIncidents, t]);

  const filtered = useMemo(() => {
    let list = incidents;
    const activeSev = urlSev || (incidentFilter !== "all" ? incidentFilter : null);
    if (activeSev) list = list.filter((i) => i.severity === activeSev);
    if (urlSla === "breach") list = list.filter((i) => i.sla > 75);
    return list;
  }, [incidents, incidentFilter, urlSev, urlSla]);

  return (
    <div data-testid="incidents-view">
      <ObserverBanner />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(26px, 3.4vw, 34px)",
              color: "#f0f2f8",
              letterSpacing: "-0.5px",
              lineHeight: 1.05,
            }}
            data-testid="view-title"
          >
            {t("nav.all_incidents")}
          </h1>
        </div>
        <RoleGuard minRole="responder">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Iter37 — List/Kanban segmented control */}
            <div
              className="inline-flex rounded-md overflow-hidden"
              style={{ border: "1px solid rgba(255,255,255,0.12)" }}
              data-testid="incidents-view-toggle"
              role="group"
              aria-label="View mode"
            >
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className="px-3 py-2 transition-colors"
                style={{
                  background: viewMode === "list" ? "rgba(24,95,165,0.18)" : "transparent",
                  color: viewMode === "list" ? "#9ec3ec" : "var(--text-dim, #c5cad8)",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 11,
                  letterSpacing: "0.5px",
                  cursor: "pointer",
                  border: "none",
                  borderRight: "1px solid rgba(255,255,255,0.12)",
                }}
                data-testid="incidents-view-list-btn"
                aria-pressed={viewMode === "list"}
              >
                {t("incidents.view_list")}
              </button>
              <button
                type="button"
                onClick={() => setViewMode("kanban")}
                className="px-3 py-2 transition-colors"
                style={{
                  background: viewMode === "kanban" ? "rgba(24,95,165,0.18)" : "transparent",
                  color: viewMode === "kanban" ? "#9ec3ec" : "var(--text-dim, #c5cad8)",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 11,
                  letterSpacing: "0.5px",
                  cursor: "pointer",
                  border: "none",
                }}
                data-testid="incidents-view-kanban-btn"
                aria-pressed={viewMode === "kanban"}
              >
                {t("incidents.view_kanban")}
              </button>
            </div>
            <Link
              to="/incidents/new/chat"
              className="flex items-center gap-1.5 px-3 py-2 rounded transition-all"
              style={{
                background: "rgba(155,89,182,0.15)",
                border: "1px solid rgba(155,89,182,0.55)",
                color: "#c89adb",
                fontFamily: "Syne, sans-serif",
                fontWeight: 600,
                fontSize: 13,
                letterSpacing: "0.3px",
                cursor: "pointer",
                textDecoration: "none",
              }}
              data-testid="incidents-create-with-ai-btn"
            >
              {t("buttons.create_with_ai")}
            </Link>
            <Link
              to="/incidents/new"
              className="flex items-center gap-1.5 px-3 py-2 rounded transition-all"
              style={{
                background: "#185FA5",
                border: "1px solid #185FA5",
                color: "#fff",
                fontFamily: "Syne, sans-serif",
                fontWeight: 600,
                fontSize: 13,
                letterSpacing: "0.3px",
                cursor: "pointer",
                textDecoration: "none",
              }}
              data-testid="register-incident-btn"
            >
              <Plus size={14} /> {t("buttons.register_incident").replace(/^＋\s*/, "")}
            </Link>
          </div>
        </RoleGuard>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {filterPills.map((p) => {
            const active = incidentFilter === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setIncidentFilter(p.id)}
                className="px-3 py-1.5 rounded-full"
                style={{
                  background: active ? "#185FA5" : "transparent",
                  border: `1px solid ${active ? "#185FA5" : "rgba(255,255,255,0.1)"}`,
                  color: active ? "#fff" : "#9aa1b3",
                  fontFamily: "DM Mono, monospace",
                  fontSize: 10,
                  letterSpacing: "0.4px",
                }}
                data-testid={`filter-pill-${p.id}`}
              >
                {p.label} ({p.count})
              </button>
            );
          })}
        </div>
      </div>

      <IncidentFilterPresets />

      <IncidentFilters
        tenants={tenants}
        plants={plants}
        totalCount={allIncidents.length}
        filteredCount={filtered.length}
        expanded={filtersExpanded}
        onToggle={() => setFiltersExpanded((v) => !v)}
      />

      {viewMode === "kanban" ? (
        <IncidentsKanban
          incidents={filtered}
          onCardClick={(id) => openPanel(id)}
        />
      ) : (
        <div data-testid="incidents-list-body">
          {filtered.map((inc) => (
            <Row key={inc.id} inc={inc} onClick={() => openPanel(inc.id)} />
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-10" style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9198b0" }}>
              {t("incidents.no_match_filter")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
